import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { TableCell, TableRow } from '@mui/material'
import Content from 'components/content/content'
import CONSTANT from 'modules/constant'
import GLOBAL from 'modules/global'
import type { ErrorProps } from 'modules/types'
import type { CarInterestHistory, PartialCarInterestHistory } from 'modules/carinterest/car-interest.dto'
import Notification from 'components/notification'
import { useDownloadCarInterestListMutation, useGetCarInterestListMutation } from 'store/carInterest'

const CarInterestList = () => {
  const [getListSubmissions, submissionsList] = useGetCarInterestListMutation()
  const [downloadCarInterestList, download] = useDownloadCarInterestListMutation()

  const [payload, setPayload] = useState<PartialCarInterestHistory>({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    search: ''
  })

  const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: value })
  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })

  const getField = () =>
    columns
      .filter((c) => c.searchable !== false)
      .map((c) => c.key)
      .join(',')

  const onResync = () => {
    const formData = new FormData()

    formData.append('start', payload.start.toString())
    formData.append('length', payload.length.toString())
    formData.append('field', getField())

    if (!isEmpty(payload.search)) formData.append('search[value]', payload.search)

    getListSubmissions(formData)
  }

  const onDownload = (type?: string) => {
    const formData = new FormData()

    formData.append('start', String(0))
    formData.append('length', CONSTANT.DEFAULT_PAGINATION_MAXIMUM_DOWNLOAD.toString())
    formData.append('field', getField())

    if (!isEmpty(payload.search)) formData.append('search[value]', payload.search)
    if (type && !isEmpty(type)) formData.append('Download-Type', type)

    downloadCarInterestList(formData)
  }

  const columns = [
    {
      column: 'Member ID',
      label: 'Member ID',
      searchable: true,
      key: 'memberCode'
    },
    {
      column: 'Full Name',
      label: 'Full Name',
      searchable: true,
      key: 'fullName'
    },
    {
      column: 'Car Brand Ownership',
      label: 'Car Brand Ownership',
      searchable: true,
      key: 'carBrandOwnership'
    },
    {
      column: 'Car Model Ownership',
      label: 'Car Model Ownership',
      searchable: true,
      key: 'carModelOwnership'
    },
    {
      column: 'Brand Interest',
      label: 'Brand Interest',
      searchable: true,
      key: 'brandInterest'
    },
    {
      column: 'Car Type Interest',
      label: 'Car Type Interest',
      searchable: true,
      key: 'carTypeInterest'
    },
    {
      column: 'Hyundai Model Interest',
      label: 'Hyundai Model Interest',
      searchable: true,
      key: 'carModelInterest'
    },
    {
      column: 'Intention to buy new car',
      label: 'Intention to buy new car',
      searchable: true,
      key: 'intentionToBuy'
    },
    {
      column: 'Form Submission Date',
      label: 'Form Submission Date',
      key: 'submissionDateString',
      searchable: false,
    }
  ]

  const tableHeadTitles = columns.map((c) => c.label)

  const totalPage = submissionsList.data && GLOBAL.tableTotalPage(submissionsList.data.recordsTotal)
  const numbers = GLOBAL.tableNumber(payload.start)

  useEffect(() => {
    onResync()
  }, [payload])

  return (
    <>
      <Content
        title='Car Interest Questionnaire List'
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onResync={onResync}
        onDownload={onDownload}
        downloadLoading={download.isLoading}
        onDownloadTypeIgnored={['pdf']}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={submissionsList.data && submissionsList.data.recordsTotal}
        page={payload.start + 1}
        isLoading={submissionsList.isLoading}
        useAction={false}
        useDownloadDropdown
      >
        {submissionsList.isSuccess &&
          submissionsList.data &&
          submissionsList.data.data?.map((table, index) => (
            <TableRow key={index}>
              <TableCell>{numbers[index] + 1}</TableCell>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  {table[col.key as keyof CarInterestHistory] || '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </Content>

      <Notification
        open={submissionsList.isError}
        onClose={() => {
          submissionsList.reset()
        }}
        isError={submissionsList.isError}
        message={GLOBAL.returnExceptionMessage(
          submissionsList.isError,
          submissionsList.error as ErrorProps
        )}
      />
    </>
  )
}

export default CarInterestList